import apiClient from './API';
import Papa from 'papaparse';
import moment from 'moment';

export const getTrackingImageProxyUrl = (imageUrl) => {
    if (!imageUrl) return '';
    return `${apiClient.defaults.baseURL}/tracking/image-proxy?url=${encodeURIComponent(imageUrl)}`;
};

// -------------------------
// BASKET CRUD
// -------------------------

export const listBaskets = async (accountId, options = {}) => {
    const params = { ...options };
    if (accountId) params.accountId = accountId;

    const response = await apiClient.get('/tracking/baskets', { params });
    return response.data;
};

export const createBasket = async ({ name, asins, country, accountId, importHistory }) => {
    const response = await apiClient.post('/tracking/baskets', { name, asins, country, accountId, importHistory });
    return response.data;
};

export const updateBasket = async (id, { name, asins, country }) => {
    const response = await apiClient.put(`/tracking/baskets/${id}`, { name, asins, country });
    return response.data;
};

export const patchBasket = async (id, payload) => {
    const response = await apiClient.patch(`/tracking/baskets/${id}`, payload);
    return response.data;
};

export const deleteBasket = async (id) => {
    const response = await apiClient.delete(`/tracking/baskets/${id}`);
    return response.data;
};

export const backfillBasket = async (basketId, accountId) => {
    const response = await apiClient.post(`/tracking/baskets/${basketId}/backfill`, { accountId });
    return response.data;
};

export const getJungleScoutTokens = async () => {
    const response = await apiClient.get('/tracking/jungle-scout-tokens');
    return response.data;
};

// -------------------------
// DATA FETCH & RETRIEVE
// -------------------------

export const fetchCompetitorData = async (basketId, accountId) => {
    const response = await apiClient.post('/tracking/fetch-data', { basketId, accountId });
    return response.data;
};

export const fetchCompetitorDataFromList = async (asins, accountId, country = 'US') => {
    const response = await apiClient.post('/tracking/fetch-data', { asins, accountId, country });
    return response.data;
};

export const getCompetitorData = async (basketId, accountId, dateFrom, dateTo) => {
    const params = { accountId };
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    const response = await apiClient.get(`/tracking/data/${basketId}`, { params });
    return response.data;
};

export const getLatestCompetitorData = async (basketId, accountId) => {
    const response = await apiClient.get(`/tracking/data/${basketId}/latest`, { params: { accountId } });
    return response.data;
};

// -------------------------
// SCHEDULER CONFIG
// -------------------------

export const getSchedulerConfig = async () => {
    const response = await apiClient.get('/tracking/config');
    return response.data;
};

export const updateSchedulerConfig = async (config) => {
    const response = await apiClient.put('/tracking/config', config);
    return response.data;
};

export const toggleBasketAutoFetch = async (basketId) => {
    const response = await apiClient.put(`/tracking/baskets/${basketId}/auto-fetch`);
    return response.data;
};

// -------------------------
// MEDIA TRACKING (SerpAPI)
// -------------------------

export const fetchProductMedia = async (asin, country = 'us') => {
    const response = await apiClient.post('/tracking/media', { asin, country });
    return response.data;
};

export const getStoredProductMedia = async (asin, marketplace) => {
    const params = marketplace ? { marketplace } : {};
    const response = await apiClient.get(`/tracking/media/${asin}`, { params });
    return response.data;
};

export const getStoredProductMediaBulk = async (asins, marketplace) => {
    const response = await apiClient.post('/tracking/media/bulk', { asins, marketplace });
    return response.data;
};

// -------------------------
// MEDIA CHANGE HISTORY
// -------------------------

export const getMediaChangeHistory = async (accountId, options = {}) => {
    const response = await apiClient.get('/tracking/media-changes', {
        params: { accountId, ...options },
    });
    return response.data;
};

export const markChangeReviewed = async (changeId) => {
    const response = await apiClient.patch(`/tracking/media-changes/${changeId}/reviewed`);
    return response.data;
};

export const getMediaChangeSummary = async (accountId, days = 30) => {
    const response = await apiClient.get('/tracking/media-changes/summary', {
        params: { accountId, days },
    });
    return response.data;
};

// -------------------------
// FILE ANALYSIS (Phase 3)
// -------------------------

export const analyzeNgram = async (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.asin) formData.append('asin', options.asin);
    if (options.nValues) formData.append('nValues', JSON.stringify(options.nValues));
    if (options.minCharLen) formData.append('minCharLen', options.minCharLen);
    if (options.excludeContains?.length) formData.append('excludeContains', JSON.stringify(options.excludeContains));

    const response = await apiClient.post('/analysis/ngram', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
    });
    return response.data;
};

export const analyzeCampaignsEvolution = async (w1, w2, w3) => {
    const formData = new FormData();
    formData.append('w1', w1);
    formData.append('w2', w2);
    formData.append('w3', w3);

    const response = await apiClient.post('/analysis/campaigns', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

export const analyzePlacementsSP = async (file, portfolio) => {
    const formData = new FormData();
    formData.append('file', file);
    if (portfolio) formData.append('portfolio', portfolio);

    const response = await apiClient.post('/analysis/placements-sp', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

export const analyzePlacementsSB = async (file, portfolio) => {
    const formData = new FormData();
    formData.append('file', file);
    if (portfolio) formData.append('portfolio', portfolio);

    const response = await apiClient.post('/analysis/placements-sb', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

export const analyzeParameters = async (file, targetAcos, portfolio) => {
    const formData = new FormData();
    formData.append('file', file);
    if (targetAcos) formData.append('targetAcos', targetAcos);
    if (portfolio) formData.append('portfolio', portfolio);

    const response = await apiClient.post('/analysis/parameters', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000, // 2 minutes for large Excel files
    });
    return response.data;
};

export const analyzeSalesEstimate = async (asins, marketplace = 'us', startDate, endDate) => {
    const body = { asins, marketplace };
    if (startDate) body.startDate = startDate;
    if (endDate) body.endDate = endDate;

    const response = await apiClient.post('/analysis/sales-estimate', body);
    return response.data;
};

export const getSalesEstimateTokens = async () => {
    const response = await apiClient.get('/analysis/sales-estimate/tokens');
    return response.data;
};

export const getCachedAnalysis = async (profileId, type) => {
    const response = await apiClient.get(`/analysis/cached/${profileId}/${type}`);
    return response.data;
};

// -------------------------
// FILE ANALYSIS — API MODE (Phase 3b)
// Uses Amazon Ads Reporting API via backend
// New flow: backend responds immediately with 202 + jobId,
// frontend polls every 3s until the job is complete.
// -------------------------

// ── Polling helpers ────────────────────────────────────────────────────────

/**
 * Consulta el estado de un job en background.
 * @returns {{ success: boolean, data: { status: 'processing'|'completed'|'failed', error?: string } }}
 */
export const getAnalysisJobStatus = async (jobId) => {
    const response = await apiClient.get(`/analysis/status/${jobId}`, { timeout: 90000 });
    return response.data;
};

/**
 * Obtiene el resultado final de un job completado.
 */
export const getAnalysisJobResult = async (jobId) => {
    const response = await apiClient.get(`/analysis/result/${jobId}`, { timeout: 30000 });
    return response.data;
};

/**
 * Espera (polling) hasta que un job termine.
 * Si el backend devolvió 200 (cache HIT), resuelve inmediatamente.
 *
 * @param {object}   initialResponse   La respuesta inicial del POST (puede ser 200 con data o 202 con jobId)
 * @param {Function} [onProgress]      Callback opcional: (status, attempt) => void
 * @param {number}   [intervalMs=5000] Intervalo entre intentos
 * @param {number}   [maxAttempts=600] Máximo de intentos (~50 min con 5s de intervalo)
 */
export const pollForResult = async (initialResponse, onProgress, intervalMs = 5000, maxAttempts = 600) => {
    // Si el backend respondió con data directamente (cache HIT), lo devolvemos sin polling
    if (initialResponse.success !== undefined && !initialResponse.jobId) {
        return initialResponse;
    }

    const { jobId } = initialResponse;
    if (!jobId) throw new Error('El servidor no devolvió un jobId');

    for (let i = 0; i < maxAttempts; i++) {
        await new Promise(r => setTimeout(r, intervalMs));

        const statusRes = await getAnalysisJobStatus(jobId);
        const status = statusRes?.data?.status;

        if (onProgress) onProgress(status, i + 1);

        if (status === 'completed') {
            return await getAnalysisJobResult(jobId);
        }

        if (status === 'failed') {
            throw new Error(statusRes?.data?.error || 'El análisis falló en el servidor');
        }
    }

    throw new Error('Timeout: El análisis tardó demasiado. Intenta de nuevo más tarde.');
};

// ── Funciones de análisis con nuevo flujo polling ─────────────────────────

export const checkMissingDaysNgram = async (profileId, startDate, endDate) => {
    const response = await apiClient.post('/analysis/ngram-api', { profileId, startDate, endDate, checkOnly: true }, { timeout: 30000 });
    return response.data;
};

export const analyzeNgramFromAPI = async (profileId, startDate, endDate, options = {}) => {
    const body = { profileId, startDate, endDate };
    if (options.asin) body.asin = options.asin;
    if (options.nValues) body.nValues = options.nValues;
    if (options.minCharLen) body.minCharLen = options.minCharLen;
    if (options.excludeContains?.length) body.excludeContains = options.excludeContains;

    // Timeout corto: el backend responde inmediatamente con 202 o 200 (cache hit)
    const response = await apiClient.post('/analysis/ngram-api', body, { timeout: 30000 });

    // Polling hasta que el job en background termine
    return await pollForResult(response.data, options.onProgress);
};

export const analyzeCampaignsFromAPI = async (profileId, weeks, onProgress) => {
    const response = await apiClient.post('/analysis/campaigns-api', {
        profileId, ...weeks,
    }, { timeout: 30000 });
    return await pollForResult(response.data, onProgress, 5000, 600);
};
export const checkMissingDaysPlacements = async (profileId, startDate, endDate, type) => {
    const response = await apiClient.post(`/analysis/placements-${type}-api`, { profileId, startDate, endDate, type, checkOnly: true }, { timeout: 30000 });
    return response.data;
};

export const analyzePlacementsSPFromAPI = async (profileId, startDate, endDate, portfolio, onProgress) => {
    const body = { profileId, startDate, endDate };
    if (portfolio) body.portfolio = portfolio;

    const response = await apiClient.post('/analysis/placements-sp-api', body, { timeout: 30000 });
    return await pollForResult(response.data, onProgress, 5000, 600);
};

export const analyzePlacementsSBFromAPI = async (profileId, startDate, endDate, portfolio, onProgress) => {
    const body = { profileId, startDate, endDate, forceRefresh: true }; // forceRefresh: skip stale cache from old config
    if (portfolio) body.portfolio = portfolio;

    const response = await apiClient.post('/analysis/placements-sb-api', body, { timeout: 30000 });
    return await pollForResult(response.data, onProgress, 5000, 600);
};

export const analyzeParametersFromAPI = async (profileId, startDate, endDate, targetAcos, portfolio, onProgress) => {
    const body = { profileId, startDate, endDate };
    if (targetAcos) body.targetAcos = targetAcos;
    if (portfolio) body.portfolio = portfolio;

    const response = await apiClient.post('/analysis/parameters-api', body, { timeout: 30000 });
    return await pollForResult(response.data, onProgress, 5000, 600);
};

// -------------------------
// CSV BASKET PARSING (client-side)
// -------------------------


/**
 * Parse a CSV file (tracking2 basket format) client-side.
 * Returns { data: rows[] } matching the shape of getCompetitorData.
 */
export const parseCsvFile = (file) => {
    return new Promise((resolve, reject) => {
        // tracking2 fix: Normalize newlines before parsing
        // Some exported CSVs have mixed line endings that confuse PapaParse
        const reader = new FileReader();

        reader.onload = (e) => {
            const content = e.target.result;
            const safeContent = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

            Papa.parse(safeContent, {
                header: true,
                skipEmptyLines: true,
                complete: (results) => {
                    try {
                        // Helper to check multiple possible keys (case-sensitive or mapping)
                        const getVal = (row, keys) => {
                            for (const k of keys) {
                                if (row[k] !== undefined && row[k] !== null && row[k] !== '') return row[k];
                            }
                            return undefined;
                        };

                        const rows = results.data.map(row => {
                            const priceVal = getVal(row, ['product_price', 'Price']);
                            const origPriceVal = getVal(row, ['product_original_price', 'Orig. Price']);
                            const ratingVal = getVal(row, ['product_star_rating', 'Rating']);
                            const numRatingsVal = getVal(row, ['product_num_ratings', 'Reviews']);
                            const rankVal = getVal(row, ['rank', 'rank_value', 'BSR']);
                            const weekVal = getVal(row, ['week', 'Week']);
                            const discountVal = getVal(row, ['discount', 'Discount']);
                            const salesVolVal = getVal(row, ['sales_volume', 'Sales Vol.']);
                            const subCatVal = getVal(row, ['sub_category_name', 'Category']);

                            const price = parseFloat(priceVal) || null;
                            const origPrice = parseFloat(origPriceVal) || null;
                            const rating = parseFloat(ratingVal) || null;
                            const numRatings = parseInt(numRatingsVal, 10) || 0;
                            const rank = parseInt(rankVal, 10) || null;
                            const weekNum = parseInt(weekVal, 10) || null;

                            // Parse date using moment for robustness
                            let dateStr = getVal(row, ['date', 'Date']) || null;
                            if (dateStr) {
                                // Try multiple formats
                                const m = moment(dateStr, [
                                    'YYYY-MM-DD',
                                    'MM/DD/YYYY',
                                    'DD/MM/YYYY',
                                    'M/D/YYYY',
                                    'D/M/YYYY',
                                    'YYYY/MM/DD',
                                    'DD-MM-YYYY',
                                    'MM-DD-YYYY'
                                ]);
                                if (m.isValid()) {
                                    dateStr = m.format('YYYY-MM-DD');
                                } else {
                                    // Fallback to JS Date if moment fails
                                    const d = new Date(dateStr);
                                    if (!isNaN(d.getTime())) {
                                        dateStr = d.toISOString().slice(0, 10);
                                    } else {
                                        dateStr = null; // Invalid date
                                    }
                                }
                            }

                            // Compute week if missing
                            let week = weekNum;
                            if (!week && dateStr) {
                                const d = new Date(dateStr);
                                const startOfYear = new Date(d.getFullYear(), 0, 1);
                                const days = Math.floor((d - startOfYear) / 86400000);
                                week = Math.ceil((days + startOfYear.getDay() + 1) / 7);
                            }

                            // Booleans
                            const acVal = getVal(row, ['is_amazon_choice', "Amazon's Choice"]);
                            const bsVal = getVal(row, ['is_best_seller', 'Best Seller']);

                            const isAC = acVal === 'TRUE' || acVal === 'true' || acVal === '1' || acVal === '✅' || acVal === 'True';
                            const isBS = bsVal === 'TRUE' || bsVal === 'true' || bsVal === '1' || bsVal === '✅' || bsVal === 'True';

                            return {
                                asin: (getVal(row, ['asin', 'ASIN']) || '').trim(),
                                product_title: getVal(row, ['product_title', 'Title']) || '',
                                product_price: price,
                                product_original_price: origPrice,
                                product_star_rating: rating,
                                product_num_ratings: numRatings,
                                is_amazon_choice: isAC,
                                is_best_seller: isBS,
                                sales_volume: salesVolVal || '',
                                discount: parseFloat(discountVal) || null,
                                brand: getVal(row, ['brand', 'Brand']) || 'Unknown',
                                product_url: row.product_url || '',
                                product_photo: row.product_photo || '',
                                unit_price: row.unit_price || '',
                                sub_category_name: subCatVal || '',
                                rank_value: rank,
                                date: dateStr,
                                week: week,
                                week_number: week,
                            };
                        }).filter(r => r.asin);

                        // Compute price_change per asin
                        const byAsin = {};
                        rows.forEach(r => {
                            if (!byAsin[r.asin]) byAsin[r.asin] = [];
                            byAsin[r.asin].push(r);
                        });
                        Object.values(byAsin).forEach(group => {
                            group.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
                            group.forEach((r, i) => {
                                if (i === 0 || !group[i - 1].product_price || !r.product_price) {
                                    r.price_change = null;
                                } else {
                                    r.price_change = ((r.product_price - group[i - 1].product_price) / group[i - 1].product_price) * 100;
                                }
                            });
                        });

                        resolve({ data: rows });
                    } catch (err) {
                        reject(err);
                    }
                },
                error: (err) => reject(err),
            });
        };

        reader.onerror = (err) => reject(err);
        reader.readAsText(file);
    });
};

/**
 * Parse a simple CSV list with ASIN in first column:
 * B07XXXXXXX,Optional Label
 */
export const parseAsinListCsv = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const content = String(e.target.result || '');
                const lines = content
                    .split(/\r\n|\n|\r/)
                    .map(line => line.trim())
                    .filter(Boolean);

                const asins = lines
                    .map(line => line.split(',')[0])
                    .map(value => String(value || '').trim().toUpperCase())
                    .filter(Boolean);

                resolve({ data: asins });
            } catch (err) {
                reject(err);
            }
        };

        reader.onerror = () => reject(new Error('Failed to read CSV file'));
        reader.readAsText(file);
    });
};
